// Blocos que valem para vários perfis da Visão Geral (#6494): avisos do que saiu do
// normal (alertas da Vick + WhatsApp fora do ar) e "Minha caixa de entrada" do Inbox.
// Regras em src/lib/visaoGeral/avisos.ts.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, MessageCircle } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import {
  ALERTAS_POR_PERFIL, agruparAlertas, avisosDeConexoes, ordenarAvisos, resumirCaixa, temAvisos, tempoDesde, veQuedaWhatsApp,
  type AlertaVick, type Aviso, type ConexaoInbox, type ConversaEsperando, type ResumoCaixa,
} from '../../src/lib/visaoGeral/avisos';
import type { PerfilVisaoGeral } from '../../src/lib/visaoGeral/perfis';
import type { Profile } from '../../types';
import { CartaoHoje, PainelDetalhe, plural } from './componentes';

// ── Dados ──

function useAvisos(perfil: PerfilVisaoGeral): Aviso[] | null {
  const [avisos, setAvisos] = useState<Aviso[] | null>(null);
  useEffect(() => {
    let vivo = true;
    setAvisos(null);
    (async () => {
      const tipos = ALERTAS_POR_PERFIL[perfil];
      const [alertas, conexoes] = await Promise.all([
        tipos.length
          ? supabase.from('ia_alertas').select('id, tipo, titulo, severidade, link, detectado_em')
              .eq('estado', 'aberto').in('tipo', [...tipos]).order('detectado_em', { ascending: false }).limit(500)
          : Promise.resolve({ data: [], error: null }),
        veQuedaWhatsApp(perfil)
          ? supabase.from('inbox_channel_connections').select('id, display_name, status, caiu_em, atendente_id').not('atendente_id', 'is', null)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (!vivo) return;
      // Sem permissão a RLS devolve vazio, não erro: a pessoa simplesmente não vê o alerta.
      const lista = [
        ...agruparAlertas((alertas.data || []) as (AlertaVick & { tipo: string })[]),
        ...avisosDeConexoes((conexoes.data || []) as ConexaoInbox[]),
      ];
      setAvisos(ordenarAvisos(lista));
    })();
    return () => { vivo = false; };
  }, [perfil]);
  return avisos;
}

interface Caixa {
  resumo: ResumoCaixa;
  /** Tempo de resposta do relatório diário mais recente com resposta medida. */
  tempo: { mediana: number; p90: number | null; dia: string } | null;
}

/** null = carregando; false = a pessoa não tem conexão no Inbox (o bloco some). */
function useCaixaEntrada(pessoaId: string): Caixa | null | false {
  const [caixa, setCaixa] = useState<Caixa | null | false>(null);
  useEffect(() => {
    let vivo = true;
    setCaixa(null);
    (async () => {
      const { data: conexoes } = await supabase.from('inbox_channel_connections').select('id').eq('atendente_id', pessoaId);
      const ids = (conexoes || []).map((c: { id: string }) => c.id);
      if (!vivo) return;
      if (ids.length === 0) { setCaixa(false); return; }
      const [conversas, relatorio] = await Promise.all([
        supabase.from('inbox_conversas_lista').select('id, contato_nome, ultima_mensagem_em, negociacao_id')
          .in('connection_id', ids).in('status', ['open', 'pending']).eq('ultima_mensagem_direcao', 'inbound').limit(1000),
        supabase.from('inbox_relatorio_diario').select('dia, metricas').eq('atendente_id', pessoaId).order('dia', { ascending: false }).limit(7),
      ]);
      if (!vivo) return;
      const comTempo = (relatorio.data || []).find((r: any) => r.metricas?.tempo_resposta_mediana_min != null);
      setCaixa({
        resumo: resumirCaixa((conversas.data || []) as ConversaEsperando[], new Date()),
        tempo: comTempo ? {
          mediana: Number(comTempo.metricas.tempo_resposta_mediana_min),
          p90: comTempo.metricas.tempo_resposta_p90_min != null ? Number(comTempo.metricas.tempo_resposta_p90_min) : null,
          dia: String(comTempo.dia),
        } : null,
      });
    })();
    return () => { vivo = false; };
  }, [pessoaId]);
  return caixa;
}

// ── Telas ──

const COR_SEVERIDADE: Record<Aviso['severidade'], string> = {
  alta: 'bg-red-500',
  media: 'bg-amber-500',
  baixa: 'bg-gray-400',
};

export const BlocoAvisos: React.FC<{ avisos: Aviso[] | null; titulo: string }> = ({ avisos, titulo }) => {
  const agora = new Date();
  return (
    <PainelDetalhe titulo={titulo}>
      {avisos === null ? (
        <div className="space-y-2">{[0, 1].map(i => <div key={i} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />)}</div>
      ) : avisos.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 size={16} /> Nada fora do normal agora.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {avisos.map(a => {
            const conteudo = (
              <>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${COR_SEVERIDADE[a.severidade]}`} aria-label={`gravidade ${a.severidade}`} />
                <span className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-100">{a.titulo}</span>
                {a.desde && <span className="text-xs text-gray-400 whitespace-nowrap">há {tempoDesde(a.desde, agora)}</span>}
                {a.link && <ChevronRight size={16} className="shrink-0 text-gray-300" />}
              </>
            );
            return (
              <li key={a.id}>
                {a.link
                  ? <Link to={a.link} className="flex items-start gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">{conteudo}</Link>
                  : <div className="flex items-start gap-3 py-2.5">{conteudo}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </PainelDetalhe>
  );
};

const minutos = (m: number) => (m < 60 ? `${Math.round(m)} min` : `${(m / 60).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`);
const diaCurto = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

export const BlocoCaixaEntrada: React.FC<{ caixa: Caixa; abreInbox: boolean }> = ({ caixa, abreInbox }) => {
  const { resumo, tempo } = caixa;
  const agora = new Date();
  const maisAntiga = resumo.recentes[0];
  const destino = (c: ConversaEsperando) => (c.negociacao_id ? `/crm?negociacaoId=${c.negociacao_id}` : abreInbox ? '/inbox' : null);
  return (
    <PainelDetalhe titulo="Minha caixa de entrada" acao={abreInbox ? { rotulo: 'Abrir o Inbox', para: '/inbox' } : undefined}>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <CartaoHoje
          valor={resumo.recentes.length}
          rotulo={maisAntiga ? `esperando resposta · a mais antiga há ${tempoDesde(maisAntiga.ultima_mensagem_em, agora)}` : 'esperando resposta'}
          tom="atencao"
        />
        <CartaoHoje
          valor={resumo.antigas}
          rotulo="paradas há mais de 1 dia"
          dica="A última mensagem foi do cliente e ninguém respondeu há mais de 24h. Em geral é conversa já terminada: feche as que acabaram."
        />
        <CartaoHoje
          valor={tempo ? minutos(tempo.mediana) : '—'}
          rotulo={tempo ? `tempo típico de resposta em ${diaCurto(tempo.dia)}${tempo.p90 != null ? ` · 9 em 10 em até ${minutos(tempo.p90)}` : ''}` : 'tempo de resposta: ainda sem relatório'}
        />
      </div>
      {resumo.recentes.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
          {resumo.recentes.slice(0, 5).map(c => {
            const para = destino(c);
            const conteudo = (
              <>
                <MessageCircle size={16} className="shrink-0 text-emerald-600" />
                <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-800 dark:text-gray-100">{c.contato_nome || 'Contato sem nome'}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">há {tempoDesde(c.ultima_mensagem_em, agora)}</span>
              </>
            );
            return (
              <li key={c.id}>
                {para
                  ? <Link to={para} className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">{conteudo}</Link>
                  : <div className="flex items-center gap-3 py-2">{conteudo}</div>}
              </li>
            );
          })}
        </ul>
      )}
      {resumo.recentes.length > 5 && (
        <p className="text-xs text-gray-400 mt-2">E mais {plural(resumo.recentes.length - 5, 'conversa', 'conversas')} esperando.</p>
      )}
    </PainelDetalhe>
  );
};

/**
 * Avisos do perfil + caixa de entrada da pessoa, lado a lado. Some quando nenhum
 * dos dois se aplica (perfil sem avisos e pessoa sem conexão no Inbox).
 */
export const BlocosDoDia: React.FC<{ pessoa: Profile; perfil: PerfilVisaoGeral }> = ({ pessoa, perfil }) => {
  const mostraAvisos = temAvisos(perfil);
  const avisos = useAvisos(perfil);
  const caixa = useCaixaEntrada(pessoa.id);
  // O Inbox só abre para adm (RoleGuard de /inbox em App.tsx); os demais respondem pela negociação.
  const abreInbox = pessoa.role === 'adm';
  const mostraCaixa = caixa !== null && caixa !== false;
  if (!mostraAvisos && !mostraCaixa) return null;
  const titulo = perfil === 'diretoria' ? 'O que saiu do normal' : 'Avisos';
  return (
    <section aria-label="Para você agora" className={`grid gap-4 ${mostraAvisos && mostraCaixa ? 'lg:grid-cols-2' : ''}`}>
      {mostraAvisos && <BlocoAvisos avisos={avisos} titulo={titulo} />}
      {mostraCaixa && <BlocoCaixaEntrada caixa={caixa} abreInbox={abreInbox} />}
    </section>
  );
};
